import { useCallback, useEffect } from 'react';
import { useIntl } from 'umi';
import { useDecisionPoints } from './useDecisionPoints';
import { useDPTAutoSave } from './useDPTAutoSave';

interface IUseDecisionPointsListOptions {
  selectedMonth: string | null;
  maxDataTime?: string;
  isGRBPEditing: boolean;
  isNotesEditing: boolean;
  onDPTEditingChange?: (isEditing: boolean) => void;
  onSectionEditingChange?: (isEditing: boolean) => void;
}

export const useDecisionPointsList = ({
  selectedMonth,
  maxDataTime,
  isGRBPEditing,
  isNotesEditing,
  onDPTEditingChange,
  onSectionEditingChange,
}: IUseDecisionPointsListOptions) => {
  const { formatMessage } = useIntl();

  const {
    DPTRef,
    tablePointRef,
    decisionPointsData,
    setDecisionPointsData,
    originalDecisionPointsData,
    decisionPointsLoading,
    isDPTEditing,
    pendingDeletes,
    setPendingDeletes,
    fetchDecisionPoints,
    handleSaveDecisionPoints: saveDecisionPoints,
    handleAddNewRow,
    onDeleteRow,
    handleEditingChange,
  } = useDecisionPoints({ selectedMonth, formatMessage, maxDataTime: maxDataTime || '' });

  const {
    autoSaveVisible,
    hasDPTUnsavedChanges,
    isManualUpdateRef,
    setDecisionPointsEditData,
    handleDecisionPointsEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearDPTAutoSave,
  } = useDPTAutoSave(
    isDPTEditing,
    decisionPointsData,
    DPTRef,
    selectedMonth,
    pendingDeletes,
    setPendingDeletes,
    setDecisionPointsData,
  );

  // Fetch Decision Points only when maxDataTime is available
  useEffect(() => {
    if (maxDataTime) {
      fetchDecisionPoints();
    }
  }, [selectedMonth, maxDataTime, fetchDecisionPoints]);

  // Wrapper for handleSaveDecisionPoints with clearDPTAutoSave
  const handleSaveDecisionPoints = async (data: any) => {
    await saveDecisionPoints(data, clearDPTAutoSave);
  };

  // Wrapper for onDeleteRow with additional dependencies
  const handleDeleteRow = useCallback(async (record: any) => {
    await onDeleteRow(record, isManualUpdateRef, setDecisionPointsEditData);
  }, [onDeleteRow, isManualUpdateRef, setDecisionPointsEditData]);

  // Decision Points editing change handler (wrapper)
  const handleDPTEditingChange = useCallback((isEditing: boolean) => {
    handleEditingChange(isEditing);
    onDPTEditingChange?.(isEditing);

    // Notify parent component about editing state change to disable the date selection
    if (onSectionEditingChange) {
      onSectionEditingChange(isGRBPEditing || isEditing || isNotesEditing);
    }
  }, [handleEditingChange, onDPTEditingChange, onSectionEditingChange, isGRBPEditing, isNotesEditing]);

  return {
    DPTRef,
    tablePointRef,
    decisionPointsData,
    originalDecisionPointsData,
    decisionPointsLoading,
    isDPTEditing,
    fetchDecisionPoints,
    handleSaveDecisionPoints,
    handleDeleteRow,
    handleAddNewRow,
    handleDPTEditingChange,
    handleDecisionPointsEdit,
    clearDPTAutoSave,
    autoSaveVisible,
    hasDPTUnsavedChanges,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    handleEditingChange,
  };
};
